import type { LngLat, TileLayerName } from "../types";
import { quadAffine2d, type ImageQuad } from "./imageQuad";
import { MAX_CAPTURE_ZOOM, withOffscreenBasemap } from "./offscreenBasemap";
import type { PixelPoint } from "./snapping";
// La projection du dépôt, celle des loaders ET du placement calculé côté consommateur : la
// réimplémenter ici (trois lignes de Mercator inverse) offrirait exactement l'écart qu'on cherche à
// exclure — une capture posée à côté des formes qu'elle a produites.
import { lngLatToMercator, mercatorToLngLat } from "../../loaders/formats/shared";

/**
 * **Capture d'une zone de la carte en image**, pour la donner à analyser.
 *
 * L'usage visé : plutôt que d'importer un fichier de plan, l'opérateur pose une image sur la carte,
 * entoure une zone, et c'est CETTE zone qu'on analyse — fond de carte compris s'il le demande. Le
 * module ne connaît rien de cet usage : il rend une zone Mercator en PNG, rien de plus.
 *
 * ## Pourquoi une composition à la main plutôt qu'une carte MapLibre qu'on photographie
 *
 * Parce qu'une carte MapLibre ne contient pas tout ce qu'on voit d'elle. Les images de forme **SVG**
 * sont des `<img>` DOM montés DERRIÈRE le canvas (voir `MapLibreAdapter.upsertShapeImageOverlay` et
 * `imageOverlayContainer`) ; seules les images raster passent par une `image source`. L'empilement
 * réel à l'écran est :
 *
 * ```
 * BackgroundMap (tuiles) < imageOverlayContainer (images SVG) < canvas principal (images raster, formes)
 * ```
 *
 * Un `toBlob()` sur le canvas d'une carte manquerait donc **toutes les images SVG** — et un plan de
 * salon en SVG est le cas nominal. Le bug serait silencieux : une capture blanche, analysée sans
 * broncher, et des formes introuvables sans que rien ne dise pourquoi.
 *
 * D'où le montage retenu : une carte hors écran ne peint QUE le fond de carte, puis ses pixels et
 * ceux de chaque image sont composés sur un `<canvas>` 2D. Les images SVG et raster y suivent le
 * même chemin, et l'ordre d'empilement est celui que l'appelant donne.
 *
 * ## Ce que la capture ne contient JAMAIS
 *
 * Ni les formes de la carte, ni les échafaudages d'import, ni les contrôles, ni les libellés
 * ajoutés par l'éditeur : on ne rejoue pas la carte de l'opérateur, on rend le fond et les images
 * qu'on nous nomme. C'est aussi pourquoi on ne photographie pas la carte principale — ce qui
 * imposerait en prime d'y activer `preserveDrawingBuffer`, un coût de perf permanent pour un usage
 * occasionnel.
 *
 * ## Le repère
 *
 * Tout est en **EPSG:3857 (Web Mercator), nord en haut** : la capture est un rectangle Mercator
 * rendu sans rotation ni inclinaison, donc pixel -> 3857 est une simple affinité
 * ({@link capturePxToMercator}). C'est ce qui permet au consommateur d'en déduire son descripteur
 * de placement sans calage. Attention : les « mètres » de Mercator ne sont pas des mètres au sol
 * (facteur 1/cos(latitude)) — c'est déjà la convention du placement d'un fond dans cet éditeur, on
 * ne fait que la suivre.
 */

/** Circonférence équatoriale WGS84 (mètres) — l'étendue du plan Mercator sur chaque axe. */
const WORLD_METERS = 40075016.685578488;

/** Taille de tuile de MapLibre : `worldSize = 512 × 2^zoom` pixels CSS. */
const TILE_SIZE = 512;

/** Résolution visée par défaut. Au-delà, l'analyse serveur réduit de toute façon l'image
 * (`RasterBudget::WORKING_PIXELS`) : capturer plus, c'est transférer plus pour rien. */
export const DEFAULT_TARGET_PIXELS = 2_000_000;

/** Plafond par côté. Au-delà, on frôle la taille maximale de canvas/texture WebGL de certaines
 * machines, où le rendu échoue en silence (canvas vide). */
export const MAX_SIDE_PX = 4096;

// Voir `offscreenBasemap` : la constante y est définie (elle sert à monter la carte), et ce module
// la ré-exporte parce que c'est ici qu'elle a toujours été publiée.
export { MAX_CAPTURE_ZOOM };

/** Rectangle en EPSG:3857. Les coins peuvent arriver dans n'importe quel ordre — un cadre tracé à
 * la souris part d'un coin quelconque —, {@link normalizeBbox} s'en charge. */
export type MercatorBbox = { minX: number; minY: number; maxX: number; maxY: number };

/**
 * Une image à composer dans la capture, déjà posée : c'est l'APPELANT qui fournit le quad, jamais
 * ce module qui le recalcule depuis une forme.
 *
 * La raison est la même que pour la projection : la pose d'une image sur une forme (mode "contain",
 * alignements, `scale`, rotation, anti-débordement) vit dans `MapLibreAdapter`, avec les dimensions
 * réelles du bitmap qu'il est seul à connaître. La recalculer ici serait une seconde
 * implémentation, et un écart entre les deux ne se verrait qu'à l'arrivée.
 */
export type CaptureImage = {
  /** URL déjà résolue (variante CDN comprise) — SVG comme raster, le module ne fait pas la
   * différence. Doit être servie avec `Access-Control-Allow-Origin`, sans quoi la capture échoue
   * explicitement plutôt que de rendre un canvas souillé. */
  src: string;
  /** Les 4 coins lng/lat sur lesquels l'image est plaquée, dans l'ordre de {@link ImageQuad}
   * (haut-gauche, haut-droite, bas-droite, bas-gauche). */
  quad: ImageQuad;
  /** Défaut 1. Voir `ShapeImage.opacity`. */
  opacity?: number;
};

export type CaptureRegionOptions = {
  /** Voir {@link DEFAULT_TARGET_PIXELS}. */
  targetPixels?: number;
  /** Voir {@link MAX_SIDE_PX}. */
  maxSidePx?: number;
};

export type CaptureRegionParams = CaptureRegionOptions & {
  bbox: MercatorBbox;
  /** `"unset"` = aucune tuile, et alors aucune carte MapLibre n'est créée du tout. */
  basemap: TileLayerName;
  /** Du BAS vers le HAUT de l'empilement. À l'écran, les images SVG passent sous les images raster
   * (voir l'en-tête de ce fichier) : c'est à l'appelant de les trier ainsi. */
  images: CaptureImage[];
};

/**
 * Tout ce que le rendu a besoin de savoir, et tout ce que l'appelant a besoin d'en relire —
 * calculé sans DOM ni carte, donc testable seul ({@link planCapture}).
 */
export type CapturePlan = {
  width: number;
  height: number;
  /** Mètres Mercator par pixel. **Uniforme sur les deux axes**, par construction : le placement
   * qu'on déduira d'une capture n'a qu'un seul facteur d'échelle. */
  metersPerPixel: number;
  zoom: number;
  center: LngLat;
  /**
   * La zone RÉELLEMENT couverte, qui peut différer de la zone demandée de moins d'un pixel : une
   * capture fait un nombre entier de pixels, et ses pixels sont carrés. C'est cette bbox-ci, pas
   * celle qu'on a demandée, qui décrit l'image produite.
   */
  bbox: MercatorBbox;
};

export type CaptureRegionResult = CapturePlan & { blob: Blob };

/** Remet un rectangle dans l'ordre, quel que soit le coin de départ du tracé. */
export function normalizeBbox(bbox: MercatorBbox): MercatorBbox {
  return {
    minX: Math.min(bbox.minX, bbox.maxX),
    minY: Math.min(bbox.minY, bbox.maxY),
    maxX: Math.max(bbox.minX, bbox.maxX),
    maxY: Math.max(bbox.minY, bbox.maxY),
  };
}

/**
 * Les 4 coins d'une zone en lng/lat, dans l'ordre de {@link ImageQuad} : nord-ouest, nord-est,
 * sud-est, sud-ouest.
 *
 * Le coin OPPOSÉ à l'indice `i` est donc toujours `(i + 2) % 4` — c'est ce qui permet de
 * redimensionner une zone en tirant un coin, l'autre servant d'ancre.
 *
 * Une zone est un rectangle du plan Mercator, pas de l'écran : sur une carte pivotée, ses coins ne
 * sont pas alignés sur les bords de la fenêtre, et c'est volontaire — c'est ce rectangle-là qui sera
 * capturé, nord en haut, et le montrer de travers est plus honnête que de le montrer droit.
 */
export function bboxRing(bbox: MercatorBbox): [LngLat, LngLat, LngLat, LngLat] {
  const { minX, minY, maxX, maxY } = normalizeBbox(bbox);
  return [
    mercatorToLngLat([minX, maxY]),
    mercatorToLngLat([maxX, maxY]),
    mercatorToLngLat([maxX, minY]),
    mercatorToLngLat([minX, minY]),
  ];
}

/** Zone délimitée par deux points quelconques — les deux bouts d'un glisser, dans n'importe quel
 * ordre. */
export function bboxFromLngLat(a: LngLat, b: LngLat): MercatorBbox {
  const [ax, ay] = lngLatToMercator(a);
  const [bx, by] = lngLatToMercator(b);

  return normalizeBbox({ minX: ax, minY: ay, maxX: bx, maxY: by });
}

/**
 * Dimensions, caméra et zone effective d'une capture — **la seule arithmétique du module**, isolée
 * du DOM pour être testée pour elle-même.
 *
 * Tout se déduit d'une seule grandeur, les mètres par pixel : la résolution visée en donne une
 * première valeur, que les plafonds (côté, zoom) ne peuvent que faire GRANDIR (capturer moins fin),
 * jamais rétrécir. Les dimensions en pixels en découlent, puis on relit les mètres par pixel sur
 * l'axe X arrondi — c'est ce dernier aller-retour qui garantit des pixels exactement carrés, donc
 * une échelle unique, ce qu'exige tout descripteur de placement en aval.
 */
export function planCapture(rawBbox: MercatorBbox, options: CaptureRegionOptions = {}): CapturePlan {
  const bbox = normalizeBbox(rawBbox);
  const spanX = bbox.maxX - bbox.minX;
  const spanY = bbox.maxY - bbox.minY;
  if (!(spanX > 0) || !(spanY > 0) || !Number.isFinite(spanX) || !Number.isFinite(spanY)) {
    throw new Error("captureRegion : la zone à capturer est vide (largeur ou hauteur nulle).");
  }

  const targetPixels = options.targetPixels ?? DEFAULT_TARGET_PIXELS;
  const maxSidePx = options.maxSidePx ?? MAX_SIDE_PX;

  const metersPerPixel = Math.max(
    // Résolution visée : `spanX × spanY / mpp² = targetPixels`.
    Math.sqrt((spanX * spanY) / targetPixels),
    // Plafonds de côté, puis de zoom — ils ne peuvent que grossir le pixel.
    spanX / maxSidePx,
    spanY / maxSidePx,
    WORLD_METERS / (TILE_SIZE * 2 ** MAX_CAPTURE_ZOOM),
  );

  const width = Math.max(1, Math.round(spanX / metersPerPixel));
  const height = Math.max(1, Math.round(spanY / metersPerPixel));

  // Relecture sur l'axe X arrondi : pixels carrés garantis, et la zone effective s'ajuste plutôt
  // que l'échelle.
  const exactMetersPerPixel = spanX / width;
  const centerX = (bbox.minX + bbox.maxX) / 2;
  const centerY = (bbox.minY + bbox.maxY) / 2;
  const halfWidthMeters = (width * exactMetersPerPixel) / 2;
  const halfHeightMeters = (height * exactMetersPerPixel) / 2;

  return {
    width,
    height,
    metersPerPixel: exactMetersPerPixel,
    zoom: Math.log2(WORLD_METERS / (TILE_SIZE * exactMetersPerPixel)),
    center: mercatorToLngLat([centerX, centerY]),
    bbox: {
      minX: centerX - halfWidthMeters,
      maxX: centerX + halfWidthMeters,
      minY: centerY - halfHeightMeters,
      maxY: centerY + halfHeightMeters,
    },
  };
}

/**
 * Point EPSG:3857 -> pixel de la capture. Affinité pure : la capture est rendue sans rotation ni
 * inclinaison, et le plan Mercator est linéaire en pixels. Y s'inverse (le Mercator monte, une
 * image descend).
 */
export function mercatorToCapturePx(plan: CapturePlan): (point: [number, number]) => PixelPoint {
  const { bbox, metersPerPixel } = plan;
  return ([x, y]) => ({
    x: (x - bbox.minX) / metersPerPixel,
    y: (bbox.maxY - y) / metersPerPixel,
  });
}

/** Sens inverse de {@link mercatorToCapturePx} — ce qui ramène une forme détectée dans la capture
 * à sa place sur la carte. */
export function capturePxToMercator(plan: CapturePlan): (point: PixelPoint) => [number, number] {
  const { bbox, metersPerPixel } = plan;
  return ({ x, y }) => [bbox.minX + x * metersPerPixel, bbox.maxY - y * metersPerPixel];
}

/**
 * Rend la zone et renvoie le PNG.
 *
 * ⚠️ `metersPerPixel` décrit **cette image-ci**. Il ne faut PAS s'en servir pour l'échelle d'un
 * descripteur de placement : le serveur réduit l'image avant de l'analyser et rend ses formes dans
 * le repère de SON aperçu — l'échelle se calcule donc sur la largeur qu'il renvoie, jamais sur
 * celle qu'on lui a postée. Une capture réduite d'un facteur 2 donnerait sinon un plan à l'échelle
 * moitié, sans un mot d'erreur.
 */
export async function captureRegion(params: CaptureRegionParams): Promise<CaptureRegionResult> {
  const plan = planCapture(params.bbox, params);

  const canvas = document.createElement("canvas");
  canvas.width = plan.width;
  canvas.height = plan.height;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("captureRegion : contexte 2D indisponible.");

  // Fond blanc systématique : la composition peut laisser des zones transparentes (aucun fond de
  // carte, une image plus petite que la zone), et un PNG à canal alpha donne à l'analyse des
  // couleurs qu'aucun plan ne porte. On aplatit ici, une fois pour toutes.
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, plan.width, plan.height);

  if (params.basemap !== "unset") {
    await drawBasemap(context, plan, params.basemap);
  }

  const toPx = mercatorToCapturePx(plan);
  for (const image of params.images) {
    await drawCaptureImage(context, image, toPx);
  }

  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/png"));
  if (!blob) {
    // `toBlob` ne rend `null` que sur un canvas souillé ou dégénéré. Le premier cas est le seul
    // plausible ici, et il se règle côté serveur d'images (en-tête CORS), pas dans ce code.
    throw new Error(
      "captureRegion : impossible d'exporter la capture — une image ou une tuile a probablement été chargée sans en-tête CORS.",
    );
  }

  return { ...plan, blob };
}

/**
 * Peint le fond de carte, seul, sur une carte MapLibre hors écran, et rend son canvas.
 *
 * Le montage de la carte (cadrage analytique, style, attente des tuiles) vit dans
 * {@link withOffscreenBasemap}, partagé avec la lecture des bâtiments du fond vectoriel.
 */
async function drawBasemap(
  context: CanvasRenderingContext2D,
  plan: CapturePlan,
  basemap: TileLayerName,
): Promise<void> {
  await withOffscreenBasemap(plan, basemap, (map) => {
    // Recopié AVANT la destruction de la carte : après, le contexte WebGL est perdu et le canvas
    // vide. On peint directement sur la capture plutôt que de rendre un canvas intermédiaire — à
    // 4096², une copie de plus, c'est 67 Mo tenus pour rien.
    context.drawImage(map.getCanvas(), 0, 0, plan.width, plan.height);
  });
}

/** Plaque une image sur son quad, exactement comme l'éditeur la plaque à l'écran. */
async function drawCaptureImage(
  context: CanvasRenderingContext2D,
  image: CaptureImage,
  toPx: (point: [number, number]) => PixelPoint,
): Promise<void> {
  const element = await loadImage(image.src);

  // Une image SVG servie sans `width`/`height` n'a pas de dimensions naturelles : on retombe sur la
  // taille du quad à l'écran, ce qui la dessine à la bonne place, simplement sans son ratio propre.
  // Le service `file` force ces attributs à l'upload (`FileForm forceSvgToHaveWidthAndHeight`), ce
  // repli ne devrait donc jouer que pour une image venue d'ailleurs.
  const quadPx = image.quad.map((corner) => toPx(lngLatToMercator(corner))) as [
    PixelPoint,
    PixelPoint,
    PixelPoint,
    PixelPoint,
  ];
  const naturalWidth = element.naturalWidth || Math.hypot(quadPx[1].x - quadPx[0].x, quadPx[1].y - quadPx[0].y);
  const naturalHeight = element.naturalHeight || Math.hypot(quadPx[3].x - quadPx[0].x, quadPx[3].y - quadPx[0].y);

  const affine = quadAffine2d(quadPx, naturalWidth, naturalHeight);
  if (!affine) return; // quad dégénéré (aire nulle) : rien à peindre

  context.save();
  context.globalAlpha = image.opacity ?? 1;
  context.imageSmoothingQuality = "high";
  context.setTransform(...affine);
  context.drawImage(element, 0, 0, naturalWidth, naturalHeight);
  context.restore();
}

/**
 * Charge une image en mode CORS.
 *
 * `crossOrigin = "anonymous"` n'est pas une précaution : sans lui, l'image se chargerait en mode
 * "no-cors" et souillerait le canvas, dont l'export échouerait ensuite sans dire pourquoi. Avec
 * lui, une source mal configurée échoue ICI, en nommant l'URL fautive.
 */
function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const element = new Image();
    element.crossOrigin = "anonymous";
    element.onload = () => resolve(element);
    element.onerror = () =>
      reject(new Error(`captureRegion : image illisible (CORS ou URL invalide) — ${src}`));
    element.src = src;
  });
}
